import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import type { Subscription } from 'rxjs';
import { Tracking } from './tracking.service';

/**
 * Tracks the duration between the start and successful end of a router navigation.
 *
 * Starts a timer when NavigationStart fires, and stops it when NavigationEnd fires,
 * logging the elapsed time in milliseconds.
 *
 * NavigationStart fires when a user clicks a link or the router.navigagte method is called.
 *
 * Note: This measures the time taken by the Angular router to complete the navigation process,
 * including guards, resolvers, and component instantiation. It does NOT measure the time
 * taken for the component to fully render, complete change detection, or finish any async
 * operations inside the component (e.g., HTTP requests or animations). We have them stats
 * already tracked.
 */
@Component({
  selector: 'fl-router-navigation-timer',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RouterNavigationTimerComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly tracking = inject(Tracking);

  private navigations = new Map<
    number, // The unique navigation ID
    { startTime: number; fromUrl: string }
  >();

  private navigationTimeTracking?: Subscription;

  ngOnInit(): void {
    this.navigationTimeTracking = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigations.set(event.id, {
          startTime: performance.now(),
          fromUrl: this.router.url,
        });
      }

      if (event instanceof NavigationEnd) {
        const navigation = this.navigations.get(event.id);
        if (navigation !== undefined) {
          const duration = performance.now() - navigation.startTime;
          this.navigations.delete(event.id);
          this.tracking.trackCustomEvent('navigation_time', 'LoggedInShell', {
            value: duration,
            fromUrl: navigation.fromUrl, // The URL before the navigation started
            url: event.url, // The URL we are navigating to
          });
        }
      }

      // Clean up for cancelled navigations
      if (
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        if (this.navigations.has(event.id)) {
          this.navigations.delete(event.id);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.navigationTimeTracking?.unsubscribe();
  }
}
